<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Physical Bow Amazon Build</h1>
    <p>The Physical Bow Amazon focuses on using high physical damage and attack speed with a bow to clear enemies. This build does not rely on elemental damage like Frozen Arrow but uses high physical output to clear enemies, especially those without physical immunity. When facing physical immune monsters, Bone Break or Atma's Scarab can be used to break their immunity.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-bow-ama.png">
    </div>

    <div class="section">
        <h2>Recommended Gear</h2>
        <Equipments
          class="right"
          :left-weapon="weapon"
          :helm="helm"
          :armor="armor"
          :gloves="gloves"
          :belt="belt"
          :boots="boots"
        />
        <h3>Weapon</h3>
        <ul>
            <li><strong>Faith</strong> – Provides “Fanaticism” aura, boosting attack speed and damage.</li>
            <li><strong>Windforce</strong> – High physical damage and attack speed, perfect for a physical Amazon.</li>
        </ul>

        <h3>Helmet</h3>
        <ul>
            <li>Three-socketed helmet with Enhanced Damage (ED) and Increased Attack Speed (IAS) jewels</li>
            <li>Crown of Ages – High resistances and damage reduction</li>
        </ul>
        
        <h3>Armor</h3>
        <ul>
            <li><strong>Enigma</strong> – Adds teleportation, great for mobility and positioning.</li>
            <li><strong>Fortitude</strong> – Provides physical damage and defense bonuses, increasing survivability.</li>
        </ul>
        
        <h3>Gloves</h3>
        <ul>
          <li><strong>Laying of Hands</strong> – Increases attack speed. Increases damage against demons.</li>
        </ul>
        
        <h3>Belt</h3>
        <ul>
            <li><strong>Razor Tail</strong> – Increases piercing chance, allowing arrows to hit more enemies.</li>
        </ul>
        
        <h3>Boots</h3>
        <ul>
            <li><strong>War Traveler</strong> – Extra damage and movement speed.</li>
        </ul>
        
        <h3>Rings</h3>
        <ul>
            <li><strong>Raven Frost</strong> – Provides Cannot Be Frozen and increased attack rating.</li>
            <li><strong>Life Leech and Mana Leech Rings</strong> – Life steal and mana steal for survival.</li>
        </ul>
        
        <h3>Amulet</h3>
        <ul>
            <li><strong>Atma's Scarab</strong> – Provides the Amplify Damage curse, helping break physical immunities.</li>
            <li><strong>Highlord's Wrath</strong> – Boosts attack speed and critical strike chance.</li>
        </ul>
        
        <h3>Inventory</h3>
        <ul>
            <li><strong>Bone Break</strong> – Used to break physical immunities of monsters.</li>
        </ul>
    </div>

    <div id="slot-content_mpu" class="content-ad-mpu"/>
    
    <div class="section">
        <h2>Skill Allocation</h2>
        <div class="m-3 skills-tab right">
          <b-tabs small content-class="m-2" class="tab-text">
            <b-tab title="Javelin">
              <img class="w-100" src="@/assets/builds/skills-bow-ama-1.png">
            </b-tab>
            <b-tab title="Passive & Magic">
              <img class="w-100" src="@/assets/builds/skills-bow-ama-2.png">
            </b-tab>
            <b-tab title="Bow" active>
              <img class="w-100" src="@/assets/builds/skills-bow-ama-3.png">
            </b-tab>
          </b-tabs>
        </div>
        <h3>Core Skills (Max Level)</h3>
        <ul>
            <li><strong>Multishot</strong> – Max out for increased attack range and physical damage.</li>
            <li><strong>Strafe</strong> – Max out for faster attack speed and multi-target damage.</li>
            <li><strong>Guided Arrow</strong> – Max out to increase bow damage and help target individual enemies.</li>
        </ul>
        
        <h3>Support Skills</h3>
        <ul>
            <li><strong>Pierce</strong> – Add some points to increase arrow penetration, hitting more enemies.</li>
            <li><strong>Critical Strike</strong> – Max out to improve the chance of critical hits and boost damage.</li>
            <li><strong>Valkyrie</strong> – 1 point, summon a sturdy Valkyrie to draw enemy attacks and improve survival.</li>
            <li><strong>Inner Sight</strong> – 1 point as needed to lower enemy defense and improve hit chances.</li>
        </ul>

        <h3>Defensive Skills</h3>
        <ul>
            <li><strong>Dodge</strong> – Add 1 point to increase the chance of dodging attacks and improving survivability.</li>
            <li><strong>Avoid</strong> – Add 1 point to avoid enemy attacks for better safety.</li>
            <li><strong>Evade</strong> – Add 1 point to provides a chance that you "Dodge" an attack while walking or running.</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Stat Allocation</h2>
        <ul>
            <li><strong>Strength:</strong> Add enough points to equip your gear.</li>
            <li><strong>Dexterity:</strong> Max out for increased attack rating and critical hit chances.</li>
            <li><strong>Vitality:</strong> Put remaining points into Vitality to increase health.</li>
            <li><strong>Energy:</strong> Do not allocate any points; rely on equipment for mana regeneration.</li>
        </ul>
    </div>

    <div class="section">
        <h2>Mercenary Recommendation</h2>
        <h3>Option 1: Act 2 Might Aura Mercenary</h3>
        <ul>
            <li><strong>Weapon:</strong> Pride – Provides “Concentration” aura and increasing attack damage.</li>
            <li><strong>Helmet:</strong> Andariel's Visage – Increases attack speed.</li>
            <li><strong>Armor:</strong> Fortitude – Boosts physical damage and defense for increased survivability.</li>
        </ul>
        
        <h3>Option 2: Act 1 Rogue Mercenary</h3>
        <ul>
            <li><strong>Weapon:</strong> Mist Runeword – Provides Concentration Aura, increasing attack damage.</li>
        </ul>
    </div>

    <div class="section">
        <h2>Combat Strategy</h2>
        <ul>
            <li>Use <strong>Multishot</strong> or <strong>Strafe</strong> to clear groups of enemies quickly.</li>
            <li>Summon a <strong>Valkyrie</strong> or <strong>Decoy</strong> to draw enemy fire and protect yourself.</li>
            <li>Use<strong>Slow Missiles</strong> slows and reduces the damage from ranged enemies' attacks/projectiles. Use <strong>Inner Sight</strong> to lower enemy defense, ensuring higher hit chances.</li>
            <li>For physical immune monsters, use <strong>Bone Break</strong> or <strong>Atma's Scarab</strong> to break their physical immunity.</li>
        </ul>
    </div>
    
    <p>The Physical Bow Amazon is a powerful ranged build that uses high physical damage, piercing, and attack speed to eliminate enemies quickly. With proper gear, she can handle a variety of monsters and, when needed, break through physical immunities. This makes her an excellent choice for both PvE and high-end farming.</p>

  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'BowAmazon',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-stagbow',
        'width': 80,
        'title': 'Faith Runeword',
        'runes': ' Ohm + Jah + Lem + Eld',
        'detail': [
          'Level 12-15 Fanaticism Aura When Equipped',
          '+1-2 To All Skills',
          '+330% Enhanced Damage',
          "Ignore Target's Defense",
          '300% Bonus To Attack Rating',
          '+75% Damage To Undead',
          '+50 To Attack Rating Against Undead',
          '+120 Fire Damage',
          'All Resistances +15',
          '10% Reanimate As: Returned',
          '75% Extra Gold From Monsters',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-diadem',
        'width': 75,
        'title': "Diadem (with 3 sockets)",
        'detail': [
          'Defense: 50-60',
          '+120% Enhanced Damage',
          '+45% Increased Attack Speed',
          'Socketed 3'
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Enigma Runeword',
        'runes': 'Jah + Ith + Ber',
        'detail': [
          '+2 To All Skills', 
          '+45% Faster Run/Walk',
          '+1 To Teleport',
          '+750-775 Defense', 
          '0-74 To Strength (Based On Character Level)',
          'Increase Maximum Life 5%',
          'Damage Reduced By 8%',
          '+14 Life After Each Kill',
          '15% Damage Taken Goes To Mana',
          '+1-99% Better Chance of Getting Magic Items (Based On Character Level)'
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-laying-of-hands',
        'width': 70,
        'title': "Laying of Hands",
        'detail': [
          'Defense: 79-87', 
          '+25 Defense',
          '+20% Increased Attack Speed',
          '+350% Damage To Demons', 
          'Fire Resist +50%',
          '10% Chance To Cast Level 3 Holy Bolt On Striking',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-light-belt',
        'width': 75,
        'title': 'Razortail',
        'detail': [
          'Defense: 96-107', 
          '+120-150% Enhanced Defense',
          '+15 Defense',
          '+10 To Maximum Damage', 
          'Piercing Attack (33)',
          '+15 To Dexterity',
          'Attacker Takes Damage of 1-99 (Based On Character Level)',
        ],
      }
    },


    boots () {
      return {
        'img': 'eq-light-plate-boots',
        'width': 70,
        'title': 'War Traveler',
        'detail': [
          'Defense: 120-139', 
          '+150-190% Enhanced Defense',
          '25% Faster Run/Walk',
          '+10 To Vitality', 
          '+10 To Strength',
          'Adds 15-25 Damage',
          '40% Slower Stamina Drain',
          'Attacker Takes Damage Of 5-10',
          '30-50% Better Chance of Getting Magic Items',
        ],
      }
    },
  }
}
</script>
